import React from 'react';
import { Box, Typography, Tooltip, Paper, Skeleton } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { OrganizedRankings } from '../types';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Filler,
  ChartOptions,
  ScaleOptions,
  Scale,
  Chart
} from 'chart.js';
import { getCountryFlag } from '../utils/marketUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Filler
);

interface PeriodPerformanceSummaryProps {
  rankings: OrganizedRankings[];
  loading?: boolean;
}

interface KeywordChange {
  keyword: string;
  oldCTR: number;
  newCTR: number;
  percentageChange: number;
  searchVolume?: number;
  market: number;
  oldPosition: number;
  newPosition: number;
}

interface WeeklyData {
  ctr: number[];
  growth_opportunity: number[];
  labels: string[];
}

const PeriodPerformanceSummary: React.FC<PeriodPerformanceSummaryProps> = ({ 
  rankings,
  loading = false
}) => {
  // Chart options and data
  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        display: false,
        grid: {
          display: false
        },
        reverse: true // This will show most recent data on the left
      },
      y: {
        display: false,
        grid: {
          display: false
        },
        beginAtZero: false,
        // Add suggestedMin and suggestedMax instead of min/max for dynamic scaling
        suggestedMin: (scale: Scale) => {
          const values = (scale.chart as Chart).data.datasets[0].data as number[];
          const min = Math.min(...values);
          return min - (min * 0.1); // 10% padding below minimum
        },
        suggestedMax: (scale: Scale) => {
          const values = (scale.chart as Chart).data.datasets[0].data as number[];
          const max = Math.max(...values);
          return max + (max * 0.1); // 10% padding above maximum
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#666',
        bodyColor: '#333',
        titleFont: {
          weight: 500,
          size: 12
        },
        bodyFont: {
          size: 12
        },
        padding: 10,
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            const date = new Date(tooltipItems[0].label);
            return date.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric',
              year: 'numeric'
            });
          },
          label: (context) => {
            const value = Math.round(context.raw as number);
            const label = context.dataset.label || '';
            return `${label}: ${value.toLocaleString()}`;
          }
        }
      }
    },
    elements: {
      point: {
        radius: 3, // Increased from 2
        hitRadius: 8, // Increased from 6
        borderWidth: 2,
        backgroundColor: '#fff'
      },
      line: {
        tension: 0.2, // Reduced from 0.3 to make changes more angular
        borderWidth: 2.5
      }
    },
    hover: {
      mode: 'index' as const,
      intersect: false
    }
  };

  const createChartData = (data: number[], color: string, label: string) => ({
    labels: metrics.labels,
    datasets: [
      {
        label,
        data,
        borderColor: color,
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 50);
          gradient.addColorStop(0, `${color}30`);
          gradient.addColorStop(1, `${color}10`);
          return gradient;
        },
        fill: true,
        borderWidth: 2.5,
        pointBackgroundColor: '#fff',
        pointBorderColor: color,
        pointBorderWidth: 2,
        pointHoverRadius: 4,
        pointHoverBorderWidth: 2,
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: color
      },
    ],
  });

  // Calculate performance metrics
  const calculateMetrics = () => {
    const changes: {
      major_increase: KeywordChange[];
      major_decrease: KeywordChange[];
      stable: KeywordChange[];
    } = {
      major_increase: [],
      major_decrease: [],
      stable: []
    };

    // Get all unique dates across all keywords and sort them chronologically (oldest to newest)
    const allDates = Array.from(new Set(
      rankings.flatMap(r =>
        r.rankings
          .filter((w): w is NonNullable<typeof w> => w !== null)
          .map(w => w.date)
      ).filter((date): date is string => date !== undefined)
    )).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    // Helper function to calculate median
    const calculateMedianValue = (values: number[]): number => {
      if (values.length === 0) return 0;
      const sorted = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
    };

    // Determine the periods for comparison
    // Ideally: last 4 weeks vs previous 4 weeks (8 total)
    // If fewer weeks available, split in half
    const totalWeeks = allDates.length;
    const weeksPerPeriod = Math.max(1, Math.floor(totalWeeks / 2));

    // Split dates into two periods
    const recentPeriodDates = allDates.slice(-weeksPerPeriod); // Last N weeks
    const priorPeriodDates = allDates.slice(-weeksPerPeriod * 2, -weeksPerPeriod); // Previous N weeks

    // For overall totals, use the most recent and comparison week
    const mostRecentDate = allDates[allDates.length - 1];
    const comparisonDate = recentPeriodDates[0]; // First week of recent period

    let currentTotalCTR = 0;
    let previousTotalCTR = 0;
    let currentTotalGrowthOpportunity = 0;
    let previousTotalGrowthOpportunity = 0;

    // Process each keyword's data
    rankings.forEach(keywordRanking => {
      // Get data for the most recent week (for totals)
      const currentWeekData = keywordRanking.rankings.find(r => r && r.date === mostRecentDate);
      // Get data for the comparison week (for totals)
      const previousWeekData = keywordRanking.rankings.find(r => r && r.date === comparisonDate);

      // Calculate current totals
      if (currentWeekData) {
        currentWeekData.products.forEach(product => {
          if (product.ctr_score) currentTotalCTR += product.ctr_score;
          if (product.growth_opportunity) currentTotalGrowthOpportunity += product.growth_opportunity;
        });
      }

      // Calculate previous totals
      if (previousWeekData) {
        previousWeekData.products.forEach(product => {
          if (product.ctr_score) previousTotalCTR += product.ctr_score;
          if (product.growth_opportunity) previousTotalGrowthOpportunity += product.growth_opportunity;
        });
      }

      // Calculate keyword-level changes using median values from periods
      // Collect CTR and position values for recent period
      const recentCTRValues: number[] = [];
      const recentPositionValues: number[] = [];
      recentPeriodDates.forEach(date => {
        const weekData = keywordRanking.rankings.find(r => r && r.date === date);
        if (weekData && weekData.products.length > 0) {
          const ctr = weekData.products.reduce((sum, p) => sum + (p.ctr_score || 0), 0);
          const positions = weekData.products
            .filter(p => p.position && p.position > 0)
            .map(p => p.position!);

          if (ctr > 0) recentCTRValues.push(ctr);
          if (positions.length > 0) {
            // Use the best (lowest) position if multiple products
            recentPositionValues.push(Math.min(...positions));
          }
        }
      });

      // Collect CTR and position values for prior period
      const priorCTRValues: number[] = [];
      const priorPositionValues: number[] = [];
      priorPeriodDates.forEach(date => {
        const weekData = keywordRanking.rankings.find(r => r && r.date === date);
        if (weekData && weekData.products.length > 0) {
          const ctr = weekData.products.reduce((sum, p) => sum + (p.ctr_score || 0), 0);
          const positions = weekData.products
            .filter(p => p.position && p.position > 0)
            .map(p => p.position!);

          if (ctr > 0) priorCTRValues.push(ctr);
          if (positions.length > 0) {
            // Use the best (lowest) position if multiple products
            priorPositionValues.push(Math.min(...positions));
          }
        }
      });

      // Calculate median CTR and position for each period
      if (recentCTRValues.length > 0 && priorCTRValues.length > 0) {
        const recentMedianCTR = calculateMedianValue(recentCTRValues);
        const priorMedianCTR = calculateMedianValue(priorCTRValues);
        const recentMedianPosition = recentPositionValues.length > 0 ? calculateMedianValue(recentPositionValues) : 0;
        const priorMedianPosition = priorPositionValues.length > 0 ? calculateMedianValue(priorPositionValues) : 0;

        if (recentMedianCTR > 0 && priorMedianCTR > 0) {
          const percentChange = ((recentMedianCTR - priorMedianCTR) / priorMedianCTR) * 100;

          // Get market from most recent data
          const market = currentWeekData?.products[0]?.market || 1;

          const change = {
            keyword: keywordRanking.keyword,
            oldCTR: priorMedianCTR,
            newCTR: recentMedianCTR,
            percentageChange: percentChange,
            searchVolume: keywordRanking.searchVolume,
            market: market,
            oldPosition: priorMedianPosition,
            newPosition: recentMedianPosition
          };

          if (Math.abs(percentChange) > 20) {
            changes[percentChange > 0 ? 'major_increase' : 'major_decrease'].push(change);
          } else {
            changes.stable.push(change);
          }
        }
      }
    });
  
    // Calculate weekly data for charts
    const weeklyData = allDates.reduce<WeeklyData>((acc, date) => {
      let totalCTR = 0;
      let totalGrowthOpportunity = 0;
  
      rankings.forEach(keywordRanking => {
        const weekData = keywordRanking.rankings.find(r => r && r.date === date);
        if (weekData) {
          weekData.products.forEach(product => {
            if (product.ctr_score) totalCTR += product.ctr_score;
            if (product.growth_opportunity) totalGrowthOpportunity += product.growth_opportunity;
          });
        }
      });
  
      acc.ctr.push(Math.round(totalCTR));
      acc.growth_opportunity.push(Math.round(totalGrowthOpportunity));
      acc.labels.push(date); // date is now guaranteed to be string due to type guard above
  
      return acc;
    }, { ctr: [], growth_opportunity: [], labels: [] });
  
    // Sort each category by absolute CTR score change (descending - biggest changes first)
    changes.major_increase.sort((a, b) => Math.abs(b.newCTR - b.oldCTR) - Math.abs(a.newCTR - a.oldCTR));
    changes.major_decrease.sort((a, b) => Math.abs(b.newCTR - b.oldCTR) - Math.abs(a.newCTR - a.oldCTR));
    changes.stable.sort((a, b) => Math.abs(b.newCTR - b.oldCTR) - Math.abs(a.newCTR - a.oldCTR));

    // Calculate overall changes
    const ctrChange = previousTotalCTR > 0
      ? ((currentTotalCTR - previousTotalCTR) / previousTotalCTR) * 100
      : 0;

    const growthOpportunityChange = previousTotalGrowthOpportunity > 0
      ? ((currentTotalGrowthOpportunity - previousTotalGrowthOpportunity) / previousTotalGrowthOpportunity) * 100
      : 0;

    return {
      changes,
      ctrScore: {
        current: Math.round(currentTotalCTR),
        change: ctrChange,
        weeklyData: weeklyData.ctr
      },
      growthOpportunity: {
        current: Math.round(currentTotalGrowthOpportunity),
        change: growthOpportunityChange,
        weeklyData: weeklyData.growth_opportunity
      },
      labels: weeklyData.labels
    };
  };
  
  const calculateTrend = (data: number[]): number => {
    const n = data.length;
    if (n < 2) return 0;

    // Ignore extreme outliers before calculations
    const sortedData = [...data].sort((a, b) => a - b);
    const q1 = sortedData[Math.floor(n * 0.25)];
    const q3 = sortedData[Math.floor(n * 0.75)];
    const iqr = q3 - q1;
    const lowerBound = q1 - (iqr * 1.5);
    const upperBound = q3 + (iqr * 1.5);
    
    // Clean data - replace outliers with nearest valid value
    const cleanData = data.map(value => {
        if (value < lowerBound) return lowerBound;
        if (value > upperBound) return upperBound;
        return value;
    });

    // Dynamic decay factor based on data length
    const decayFactor = n > 8 ? 1.2 : 1.5;
    
    // Exponential weights with reduced impact of oldest data points
    const weights = cleanData.map((_, i) => {
        // Ensure oldest data points maintain at least 30% weight
        const minWeight = 0.3;
        const weight = Math.exp((i / (n - 1)) * decayFactor);
        return minWeight + (weight - minWeight) * 0.7;
    });
    
    const weightSum = weights.reduce((a, b) => a + b, 0);
    const normalizedWeights = weights.map(w => w / weightSum);

    const x = Array.from({ length: n }, (_, i) => i);
    
    // Weighted means with clean data
    const meanX = x.reduce((sum, xi, i) => sum + xi * normalizedWeights[i], 0);
    const meanY = cleanData.reduce((sum, yi, i) => sum + yi * normalizedWeights[i], 0);
    
    let numerator = 0;
    let denominator = 0;
    
    for (let i = 0; i < n; i++) {
        const xDiff = x[i] - meanX;
        const yDiff = cleanData[i] - meanY;
        numerator += xDiff * yDiff * normalizedWeights[i];
        denominator += xDiff * xDiff * normalizedWeights[i];
    }
    
    const slope = denominator !== 0 ? numerator / denominator : 0;
    
    // Percentage change with minimum threshold to avoid micro-changes
    const percentageChange = meanY !== 0 ? (slope * (n - 1) / meanY) * 100 : 0;
    
    // Round to 2 decimal places, but return 0 for very small changes
    return Math.abs(percentageChange) < 0.1 ? 0 : Math.round(percentageChange * 100) / 100;
};



  const metrics = calculateMetrics();
  const ctrTrend = calculateTrend(metrics.ctrScore.weeklyData);
  const growthOpportunityTrend = calculateTrend(metrics.growthOpportunity.weeklyData);

  const chartDataCTR = {
    labels: metrics.labels,
    datasets: [
      {
        label: 'CTR Score',
        data: metrics.ctrScore.weeklyData,
        borderColor: '#2196f3',
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 50);
          gradient.addColorStop(0, '#2196f330');
          gradient.addColorStop(1, '#2196f310');
          return gradient;
        },
        fill: true,
        borderWidth: 2.5,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#2196f3',
        pointBorderWidth: 2,
        pointHoverRadius: 6, // Increased from 4
        pointHoverBorderWidth: 2,
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#2196f3'
      },
    ],
  };

  const chartDataGrowthOpportunity = {
    labels: metrics.labels,
    datasets: [
      {
        label: 'Growth Opportunity Score',
        data: metrics.growthOpportunity.weeklyData,
        borderColor: '#4caf50',
        backgroundColor: (context: any) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 50);
          gradient.addColorStop(0, '#4caf5030');
          gradient.addColorStop(1, '#4caf5010');
          return gradient;
        },
        fill: true,
        borderWidth: 2.5,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#4caf50',
        pointBorderWidth: 2,
        pointHoverRadius: 6, // Increased from 4
        pointHoverBorderWidth: 2,
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#4caf50'
      },
    ],
  };

  const getChangeIcon = (change: string) => {
    switch (change) {
      case 'major_increase':
        return <TrendingUpIcon sx={{ color: 'success.main' }} />;
      case 'major_decrease':
        return <TrendingDownIcon sx={{ color: 'error.main' }} />;
      default:
        return <TrendingFlatIcon sx={{ color: 'text.secondary' }} />;
    }
  };

  const getChangeColor = (change: string): string => {
    switch (change) {
      case 'major_increase':
        return 'success.main';
      case 'major_decrease':
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  const getChangeText = (change: string): string => {
    switch (change) {
      case 'major_increase':
        return 'Major Increase';
      case 'major_decrease':
        return 'Major Decrease';
      default:
        return 'Stable';
    }
  };

  const renderChangeTooltip = (changes: KeywordChange[], type: string) => {
    return (
      <Box sx={{ minWidth: 450, maxWidth: 600 }}>
        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
          {type} keywords (by CTR Score):
        </Typography>
        {changes.map((change, idx) => (
          <Box key={idx} sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {change.keyword}
              <Typography
                component="span"
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  ml: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                <span style={{ marginRight: '4px' }}>{getCountryFlag(change.market)}</span>
                {change.searchVolume && (
                  <span>
                    {change.searchVolume.toLocaleString()} searches/mo
                  </span>
                )}
              </Typography>
            </Typography>
            <Box sx={{ ml: 2, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography
                component="div"
                variant="body2"
                sx={{ color: 'text.secondary' }}
              >
                CTR: {change.oldCTR < 10 ? change.oldCTR.toFixed(1) : Math.round(change.oldCTR).toLocaleString()} → {change.newCTR < 10 ? change.newCTR.toFixed(1) : Math.round(change.newCTR).toLocaleString()}
                <span style={{
                  color: change.percentageChange > 0 ? '#4caf50' : '#f44336',
                  marginLeft: '4px'
                }}>
                  ({change.percentageChange > 0 ? '+' : ''}{change.percentageChange.toFixed(1)}%)
                </span>
              </Typography>
              {change.oldPosition > 0 && change.newPosition > 0 && (
                <Typography
                  component="div"
                  variant="body2"
                  sx={{ color: 'text.secondary' }}
                >
                  Position: {Math.round(change.oldPosition)} → {Math.round(change.newPosition)}
                  {Math.abs(change.newPosition - change.oldPosition) > 0.5 && (
                    <span style={{
                      color: change.newPosition < change.oldPosition ? '#4caf50' : '#f44336',
                      marginLeft: '4px'
                    }}>
                      ({change.newPosition < change.oldPosition ? '↑' : '↓'}{Math.abs(Math.round(change.newPosition) - Math.round(change.oldPosition))})
                    </span>
                  )}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  interface TrendIndicatorProps {
    percentageChange: number;
    size?: 'small' | 'medium' | 'large';
  }

  const TrendIndicator: React.FC<TrendIndicatorProps> = ({ percentageChange, size = 'medium' }) => {
    // Define thresholds for different states (10 states from very negative to very positive)
    const getStateInfo = (change: number) => {
      if (change <= -50) return { angle: -90, color: '#d32f2f', label: 'Severe Decline' };
      if (change <= -35) return { angle: -67.5, color: '#e57373', label: 'Major Decline' };
      if (change <= -20) return { angle: -45, color: '#ef5350', label: 'Significant Decline' };
      if (change <= -10) return { angle: -22.5, color: '#ef9a9a', label: 'Moderate Decline' };
      if (change < -2) return { angle: -10, color: '#ffcdd2', label: 'Slight Decline' };
      if (change >= 2 && change < 10) return { angle: 10, color: '#c8e6c9', label: 'Slight Improvement' };
      if (change >= 10 && change < 20) return { angle: 22.5, color: '#81c784', label: 'Moderate Improvement' };
      if (change >= 20 && change < 35) return { angle: 45, color: '#66bb6a', label: 'Significant Improvement' };
      if (change >= 35 && change < 50) return { angle: 67.5, color: '#4caf50', label: 'Major Improvement' };
      if (change >= 50) return { angle: 90, color: '#2e7d32', label: 'Exceptional Growth' };
      return { angle: 0, color: '#9e9e9e', label: 'Stable' }; // For changes between -2 and 2
    };

    const { angle, color, label } = getStateInfo(percentageChange);
  
    const sizeProps = {
      small: { iconSize: 16, boxSize: 24 },
      medium: { iconSize: 20, boxSize: 32 },
      large: { iconSize: 24, boxSize: 40 }
    }[size];

    return (
      <Tooltip 
        title={`${label} (${percentageChange > 0 ? '+' : ''}${percentageChange.toFixed(1)}%)`}
        arrow
      >
        <Box
          sx={{
            width: sizeProps.boxSize,
            height: sizeProps.boxSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            bgcolor: `${color}15`,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: `${color}25`,
            }
          }}
        >
          <ArrowUpwardIcon 
            sx={{ 
              fontSize: sizeProps.iconSize,
              color: color,
              transform: `rotate(${angle}deg)`,
              transition: 'transform 0.3s ease'
            }} 
          />
        </Box>
      </Tooltip>
    );
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        mb: 2, 
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'linear-gradient(90deg, primary.main, primary.light)'
        },
        display: loading ? 'none' : 'block'
      }}
    >
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 2,
          fontWeight: 600,
          color: 'text.primary',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: '1.1rem',
          '&::after': {
            content: '""',
            flex: 1,
            height: '1px',
            bgcolor: 'divider'
          }
        }}
      >
        Period Performance Summary
      </Typography>
      
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 4,
        '@media (max-width: 600px)': {
          flexDirection: 'column',
          gap: 3
        }
      }}>
        {/* Keyword Changes */}
        <Box sx={{ 
          flex: 1, 
          minWidth: 250,
          p: 2,
          bgcolor: 'background.default',
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography 
            variant="subtitle2" 
            color="text.secondary" 
            sx={{ 
              mb: 2.5,
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              letterSpacing: '0.1em',
              position: 'relative',
              pl: 2,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: '4px',
                height: '16px',
                bgcolor: 'primary.main',
                borderRadius: '2px'
              }
            }}
          >
            Keyword Changes (by CTR Score)
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Tooltip
              title={renderChangeTooltip(metrics.changes.major_increase, 'Major Increase')}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    maxWidth: 'none',
                    '& .MuiTooltip-arrow': {
                      color: 'background.paper'
                    }
                  }
                }
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                cursor: 'help',
                p: 1.5,
                borderRadius: 1,
                transition: 'all 0.2s ease',
                border: '1px solid',
                borderColor: 'transparent',
                '&:hover': {
                  bgcolor: 'success.lightest',
                  borderColor: 'success.light'
                }
              }}>
                <Box sx={{ 
                  bgcolor: 'success.light',
                  p: 0.75,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {getChangeIcon('major_increase')}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {metrics.changes.major_increase.length} keywords
                </Typography>
              </Box>
            </Tooltip>

            <Tooltip
              title={renderChangeTooltip(metrics.changes.major_decrease, 'Major Decrease')}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    maxWidth: 'none',
                    '& .MuiTooltip-arrow': {
                      color: 'background.paper'
                    }
                  }
                }
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                cursor: 'help',
                p: 1.5,
                borderRadius: 1,
                transition: 'all 0.2s ease',
                border: '1px solid',
                borderColor: 'transparent',
                '&:hover': {
                  bgcolor: 'error.lightest',
                  borderColor: 'error.light'
                }
              }}>
                <Box sx={{ 
                  bgcolor: 'error.light',
                  p: 0.75,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {getChangeIcon('major_decrease')}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {metrics.changes.major_decrease.length} keywords
                </Typography>
              </Box>
            </Tooltip>

            <Tooltip
              title={renderChangeTooltip(metrics.changes.stable, 'Stable')}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    maxWidth: 'none',
                    '& .MuiTooltip-arrow': {
                      color: 'background.paper'
                    }
                  }
                }
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                cursor: 'help',
                p: 1.5,
                borderRadius: 1,
                transition: 'all 0.2s ease',
                border: '1px solid',
                borderColor: 'transparent',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'divider'
                }
              }}>
                <Box sx={{ 
                  bgcolor: 'action.selected',
                  p: 0.75,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {getChangeIcon('stable')}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {metrics.changes.stable.length} keywords
                </Typography>
              </Box>
            </Tooltip>

            {/* Total Keywords */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5, 
              p: 1.5,
              borderRadius: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              mt: 1
            }}>
              <Box sx={{ 
                bgcolor: 'primary.light',
                p: 0.75,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                minWidth: 28,
                justifyContent: 'center'
              }}>
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>Σ</Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {rankings.length} total keywords
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Metrics */}
        <Box sx={{ 
          flex: 1.5,
          minWidth: 300,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          p: 2.5,
          bgcolor: 'background.default',
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography 
            variant="subtitle2" 
            color="text.secondary" 
            sx={{ 
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              letterSpacing: '0.1em',
              position: 'relative',
              pl: 2,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: '4px',
                height: '16px',
                bgcolor: 'primary.main',
                borderRadius: '2px'
              }
            }}
          >
            Performance Metrics
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2.5
          }}>
            <Box sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.05)'
              }
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  flexWrap: 'wrap',
                  gap: 1.5,
                  color: 'text.secondary'
                }}
              >
                Total CTR Score 
                <span style={{ 
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  color: 'text.primary',
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  {Math.round(metrics.ctrScore.current).toLocaleString()}
                  <TrendIndicator percentageChange={ctrTrend} size="small" />
                </span>
                <Typography 
                  component="span" 
                  variant="caption"
                  color={metrics.ctrScore.change > 0 ? 'success.main' : 'error.main'}
                  sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center',
                    fontWeight: 500,
                    bgcolor: metrics.ctrScore.change > 0 ? 'success.lightest' : 'error.lightest',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1.5,
                    fontSize: '0.75rem'
                  }}
                >
                  ({metrics.ctrScore.change > 0 ? '+' : ''}{metrics.ctrScore.change.toFixed(1)}% vs last available week)
                </Typography>
              </Typography>
              <Box sx={{ 
                height: '60px', 
                mt: 1,
                width: '100%',
                position: 'relative',
                bgcolor: 'background.paper',
                borderRadius: 1,
                p: 0.5
              }}>
                <Line
                  data={chartDataCTR}
                  options={chartOptions}
                  width="100%"
                  height="60px"
                />
              </Box>
            </Box>

            <Box sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.05)'
              }
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  flexWrap: 'wrap',
                  gap: 1.5,
                  color: 'text.secondary'
                }}
              >
                Total Growth Opportunity Score 
                <span style={{ 
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  color: 'text.primary',
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  {Math.round(metrics.growthOpportunity.current).toLocaleString()}
                  <TrendIndicator percentageChange={growthOpportunityTrend} size="small" />
                </span>
                <Typography 
                  component="span" 
                  variant="caption"
                  color={metrics.growthOpportunity.change > 0 ? 'success.main' : 'error.main'}
                  sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center',
                    fontWeight: 500,
                    bgcolor: metrics.growthOpportunity.change > 0 ? 'success.lightest' : 'error.lightest',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1.5,
                    fontSize: '0.75rem'
                  }}
                >
                  ({metrics.growthOpportunity.change > 0 ? '+' : ''}{metrics.growthOpportunity.change.toFixed(1)}% vs last available week)
                </Typography>
              </Typography>
              <Box sx={{ 
                height: '60px', 
                mt: 1,
                width: '100%',
                position: 'relative',
                bgcolor: 'background.paper',
                borderRadius: 1,
                p: 0.5
              }}>
                <Line
                  data={chartDataGrowthOpportunity}
                  options={chartOptions}
                  width="100%"
                  height="60px"
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default PeriodPerformanceSummary;
